import { FileText, Shield, User, Globe, CreditCard, AlertCircle } from 'lucide-react';

const TermsPage = () => {
  const sections = [
    {
      icon: User,
      title: 'User Accounts and Responsibilities',
      content: [
        'You must provide accurate information when creating an account',
        'You are responsible for maintaining the security of your account credentials',
        'One account per person - sharing accounts is not permitted',
        'You must be at least 16 years old to create an account',
        'Notify us immediately of any unauthorized use of your account'
      ]
    },
    {
      icon: Globe,
      title: 'Course Access and Usage',
      content: [
        'Course access is granted for personal, non-commercial use only',
        'You may not share, distribute, or resell course content',
        'Downloaded materials are for your personal learning only',
        'Course content may be updated or modified without notice',
        'Lifetime access means as long as ExecuteAutomation operates'
      ]
    },
    {
      icon: CreditCard,
      title: 'Payment and Billing',
      content: [
        'All prices are in USD unless otherwise specified',
        'Payment is required before accessing course content',
        'We accept major credit cards and approved payment methods',
        'Billing information must be current and accurate',
        'Subscription services auto-renew unless cancelled'
      ]
    },
    {
      icon: Shield,
      title: 'Intellectual Property',
      content: [
        'All course content is protected by copyright and other laws',
        'ExecuteAutomation retains all rights to course materials',
        'You may not copy, modify, or create derivative works',
        'Trademarks and logos are property of ExecuteAutomation',
        'User-generated content remains your property but grants us usage rights'
      ]
    },
    {
      icon: AlertCircle,
      title: 'Prohibited Activities',
      content: [
        'Attempting to reverse engineer or hack our platform',
        'Sharing login credentials or course access with others',
        'Uploading malicious code or harmful content',
        'Harassing other users or our staff',
        'Using our services for illegal activities'
      ]
    },
    {
      icon: FileText,
      title: 'Termination and Suspension',
      content: [
        'We may suspend or terminate accounts for terms violations',
        'You may cancel your account at any time',
        'Terminated accounts lose access to all course materials',
        'We reserve the right to modify or discontinue services',
        'Refund eligibility depends on our refund policy'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Terms of <span className="bg-gradient-to-r from-green-100 to-emerald-100 bg-clip-text text-transparent">Service</span>
            </h1>
            <p className="text-xl text-green-50 max-w-3xl mx-auto">
              Please read these terms carefully. By using our services, you agree to be bound by these terms and conditions.
            </p>
            <div className="mt-6 text-green-100">
              <p>Last updated: January 2024</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Introduction */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Welcome to <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">ExecuteAutomation</span>
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            These Terms of Service ("Terms") govern your use of ExecuteAutomation's website, courses, and services. 
            By accessing or using our platform, you agree to be bound by these Terms and our Privacy Policy.
          </p>
          <p className="text-gray-600 leading-relaxed">
            If you do not agree to these Terms, please do not use our services. We reserve the right to modify 
            these Terms at any time, and your continued use constitutes acceptance of any changes.
          </p>
        </div>

        {/* Terms Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-green-100 rounded-xl mr-4">
                    <Icon className="h-6 w-6 text-green-600" aria-label="Icon icon" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Limitation of Liability */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 mt-12">
          <h3 className="text-xl font-bold text-amber-900 mb-4">Limitation of Liability</h3>
          <p className="text-amber-800 mb-4">
            ExecuteAutomation provides educational content "as is" without warranties of any kind. 
            We do not guarantee specific outcomes from course completion.
          </p>
          <ul className="space-y-2 text-amber-800">
            <li>• We are not liable for any indirect, incidental, or consequential damages</li>
            <li>• Our total liability is limited to the amount you paid for our services</li>
            <li>• We do not guarantee employment or career advancement outcomes</li>
            <li>• Course content is for educational purposes and may become outdated</li>
          </ul>
        </div>

        {/* Governing Law */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Governing Law and <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Disputes</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Governing Law</h4>
              <p className="text-gray-600 text-sm mb-4">
                These Terms are governed by the laws of New Zealand, where ExecuteAutomation is based.
              </p>
              <h4 className="font-semibold text-gray-900 mb-2">Dispute Resolution</h4>
              <p className="text-gray-600 text-sm">
                We encourage resolving disputes through direct communication. Legal disputes will be handled 
                in the appropriate courts of New Zealand.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Contact Information</h4>
              <p className="text-gray-600 text-sm mb-2">
                For questions about these Terms, contact us at:
              </p>
              <p className="text-gray-600 text-sm">
                Email: karthik@techgeek.co.in<br/>
                Location: Auckland, New Zealand
              </p>
            </div>
          </div>
        </div>

        {/* Agreement */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 text-white mt-12">
          <h3 className="text-xl font-bold mb-4">Agreement and Updates</h3>
          <p className="text-green-100 mb-4">
            By using ExecuteAutomation's services, you acknowledge that you have read, understood, 
            and agree to be bound by these Terms of Service and our Privacy Policy.
          </p>
          <p className="text-green-100">
            We may update these Terms from time to time. Continued use of our services after changes 
            constitutes acceptance of the updated Terms.
          </p>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Questions About These <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Terms</span>?
          </h3>
          <p className="text-gray-600 mb-6">
            If you have any questions about these Terms of Service, please contact our support team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:karthik@techgeek.co.in"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all"
            >
              Email Support
            </a>
            <a 
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-green-500 hover:text-green-600 transition-colors"
            >
              Contact Form
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;